import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import * as styled from '../styles/audio-recorder';

type Props = {
  onRecordingComplete: (file: File) => void;
  onCancel: () => void;
};

const MAX_AUDIO_DURATION_MS = 120_000;
const WAVEFORM_BARS = 40;
const WAVEFORM_HEIGHT = 60;

const getMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2'))
    return 'audio/mp4;codecs=mp4a.40.2';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus'))
    return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  return '';
};

export const AudioRecorder = ({ onRecordingComplete, onCancel }: Props) => {
  const theme = useTheme();
  const [state, setState] = useState<Review.AudioRecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const stopAll = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    analyserRef.current = null;
  }, []);

  useEffect(() => stopAll, [stopAll]);

  const drawWaveform = useCallback(() => {
    const analyser = analyserRef.current;
    const canvas = canvasRef.current;
    if (!analyser || !canvas) return;

    const canvasContext = canvas.getContext('2d');
    if (!canvasContext) return;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      canvasContext.clearRect(0, 0, canvas.width, canvas.height);
      const barWidth = canvas.width / WAVEFORM_BARS;
      const step = Math.floor(dataArray.length / WAVEFORM_BARS);

      for (let index = 0; index < WAVEFORM_BARS; index++) {
        const value = dataArray[index * step] / 255;
        const barHeight = Math.max(3, value * WAVEFORM_HEIGHT);
        const x = index * barWidth;
        const y = (WAVEFORM_HEIGHT - barHeight) / 2;

        canvasContext.fillStyle = theme.colors.accent;
        canvasContext.beginPath();
        canvasContext.roundRect(x + 1, y, barWidth - 2, barHeight, 2);
        canvasContext.fill();
      }
    };

    draw();
  }, [theme.colors.accent]);

  useEffect(() => {
    if (state !== 'recording') return;
    drawWaveform();
  }, [state, drawWaveform]);

  const startRecording = () => {
    const mimeType = getMimeType();
    if (!mimeType) {
      setError('Audio recording is not supported in this browser.');
      return;
    }

    chunksRef.current = [];
    setElapsed(0);

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        streamRef.current = stream;

        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;

        const recorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunksRef.current.push(event.data);
        };

        recorder.onstop = () => {
          const contentType = mimeType.includes('mp4')
            ? 'audio/mp4'
            : 'audio/webm';
          const blob = new Blob(chunksRef.current, { type: contentType });
          blobRef.current = blob;
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
          setState('recorded');
          stopAll();
        };

        recorder.start(250);
        setState('recording');

        timerRef.current = setInterval(() => {
          setElapsed((previousElapsed) => {
            const next = previousElapsed + 1;
            if (next * 1000 >= MAX_AUDIO_DURATION_MS) {
              recorder.stop();
            }
            return next;
          });
        }, 1000);
      })
      .catch(() => {
        setError('Microphone access denied. Please allow mic permissions.');
      });
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const handleReRecord = () => {
    setAudioUrl(null);
    blobRef.current = null;
    setState('idle');
  };

  const handleConfirm = () => {
    const blob = blobRef.current;
    if (!blob) return;
    const fileExtension = blob.type.includes('mp4') ? 'm4a' : 'webm';
    const file = new File([blob], `voice-${Date.now()}.${fileExtension}`, {
      type: blob.type,
    });
    onRecordingComplete(file);
  };

  const formatTime = (secs: number) => {
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  if (error) {
    return (
      <styled.Container>
        <styled.ErrorText>{error}</styled.ErrorText>
        <styled.Body>
          <styled.Row>
            <styled.CancelBtn type="button" onClick={onCancel}>
              Cancel
            </styled.CancelBtn>
          </styled.Row>
        </styled.Body>
      </styled.Container>
    );
  }

  return (
    <styled.Container>
      {state === 'idle' && (
        <styled.Body>
          <styled.MicIcon>🎤</styled.MicIcon>
          <styled.Hint>Tap to start recording (max 2 min)</styled.Hint>
          <styled.Row>
            <styled.CancelBtn type="button" onClick={onCancel}>
              Cancel
            </styled.CancelBtn>
            <styled.PrimaryBtn type="button" onClick={startRecording}>
              Start recording
            </styled.PrimaryBtn>
          </styled.Row>
        </styled.Body>
      )}

      {state === 'recording' && (
        <styled.Body>
          <styled.RecordingDot />
          <styled.Canvas ref={canvasRef} width={280} height={WAVEFORM_HEIGHT} />
          <styled.Timer>{formatTime(elapsed)} / 2:00</styled.Timer>
          <styled.StopBtn type="button" onClick={stopRecording}>
            Stop recording
          </styled.StopBtn>
        </styled.Body>
      )}

      {state === 'recorded' && audioUrl && (
        <styled.Body>
          <styled.MicIcon>🎤</styled.MicIcon>
          <styled.Audio controls src={audioUrl} />
          <styled.Row>
            <styled.CancelBtn type="button" onClick={handleReRecord}>
              Re-record
            </styled.CancelBtn>
            <styled.PrimaryBtn type="button" onClick={handleConfirm}>
              Use recording
            </styled.PrimaryBtn>
          </styled.Row>
        </styled.Body>
      )}
    </styled.Container>
  );
};
